/**
 * Support Centre — the customers' side.
 *
 * Everything submitted through Get in Touch on the marketing site lands here,
 * oldest first, in the same four-state queue every other card uses.
 *
 * WHY THE OLDEST IS AT THE TOP
 *
 * The founder's rule for disputes — "the oldest being at the top of the list so
 * that they can be resolved on a first-in-first-out basis" — and it belongs here
 * for the same reason. Somebody who wrote in nine days ago and has heard nothing
 * is the person most likely to give up on Orchid, and they are invisible in any
 * ordering that puts the newest first.
 *
 * WHY THERE IS NO REASON GATE ON THIS ONE
 *
 * This is the only screen in the portal that shows a name and an email address
 * without a captured reason. That is deliberate: the person WROTE IN, unasked,
 * requesting contact. Their name and address are the message. Canon reserves
 * reason capture for opening a record somebody did not offer, and applying it
 * here would be ceremony that teaches staff to click through the gate.
 */

import { useCallback, useEffect, useState } from 'react';
import { O, SANS, Card, StatusPill, LoadingPanel, Notice, PrimaryCTA, QuietButton, Field, textInputStyle } from '../ui';
import Queue, { RejectionReason, StateLabel } from '../Queue';
import { fetchCustomerEnquiries, resolveEnquiry, enquiryState } from '../lib/support';
import { fetchHelpCentre, searchTerms } from '../lib/helpCentre';

const waitTone = (days) => (days >= 7 ? 'critical' : days >= 3 ? 'warning' : 'quiet');
const waitLabel = (days) => (days === 0 ? 'Today' : days === 1 ? '1 day' : `${days} days`);

const Answer = ({ row, onDone }) => {
  const [status, setStatus] = useState('answered');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState(null);

  // The database refuses a close or a spam mark without a reason. Saying so
  // here means somebody learns it before pressing, not after.
  const needsNote = status === 'closed' || status === 'spam';

  const submit = async () => {
    setBusy(true);
    setFailure(null);
    const { error } = await resolveEnquiry(row.id, status, note);
    setBusy(false);
    if (error) { setFailure(error); return; }
    onDone();
  };

  return (
    <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${O.hairline}` }}>
      {failure && <Notice tone="critical">{failure}</Notice>}
      <Field label="What happened with this enquiry?" htmlFor={`status-${row.id}`}>
        <select id={`status-${row.id}`} value={status} style={textInputStyle(false)}
                onChange={(e) => setStatus(e.target.value)}>
          <option value="answered">I have answered them</option>
          <option value="closed">Closing without an answer</option>
          <option value="spam">Spam</option>
        </select>
      </Field>
      <Field
        label={needsNote ? 'Why (required)' : 'A note, if it helps whoever reads this later'}
        htmlFor={`note-${row.id}`}
      >
        <textarea id={`note-${row.id}`} rows={2} value={note}
                  style={{ ...textInputStyle(false), resize: 'vertical' }}
                  onChange={(e) => setNote(e.target.value)} />
      </Field>
      <PrimaryCTA onClick={submit} busy={busy}
                  disabled={busy || (needsNote && note.trim().length < 4)}>
        {busy ? 'Saving…' : 'Save'}
      </PrimaryCTA>
    </div>
  );
};

const EnquiryRow = ({ row, state, onChanged }) => {
  const [open, setOpen] = useState(false);
  return (
    <Card key={row.id}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 9 }}>
        <h3 style={{ fontSize: 14.5, fontWeight: 700, color: O.ink, margin: 0 }}>{row.name}</h3>
        <StateLabel state={state} settledLabel="Answered" />
        {state === 'waiting' && (
          <StatusPill tone={waitTone(row.waiting_days)}>{waitLabel(row.waiting_days)}</StatusPill>
        )}
        {row.reference_number && <StatusPill tone="quiet">{row.reference_number}</StatusPill>}
      </div>

      <p style={{ margin: '6px 0 0', fontSize: 12.5, color: O.inkMuted }}>
        {row.email}{row.phone ? ` · ${row.phone}` : ''}
      </p>
      {row.subject && (
        <p style={{ margin: '6px 0 0', fontSize: 13, fontWeight: 600, color: O.ink }}>
          {row.subject}
        </p>
      )}
      <p style={{ margin: '8px 0 0', fontSize: 13, lineHeight: 1.7, color: O.ink, whiteSpace: 'pre-wrap' }}>
        {row.message}
      </p>

      {state === 'rejected' && <RejectionReason reason={row.resolution_note} />}
      {state === 'settled' && row.resolution_note && (
        <p style={{ margin: '6px 0 0', fontSize: 12, lineHeight: 1.6, color: O.inkMuted }}>
          <strong style={{ color: O.ink }}>Note: </strong>{row.resolution_note}
        </p>
      )}

      {state === 'waiting' && (
        <div style={{ marginTop: 12, maxWidth: 260 }}>
          <QuietButton onClick={() => setOpen((v) => !v)}>
            {open ? 'Cancel' : 'Record what you did'}
          </QuietButton>
        </div>
      )}
      {open && <Answer row={row} onDone={() => { setOpen(false); onChanged(); }} />}
    </Card>
  );
};

/**
 * THE WORDS THE CUSTOMER IS LOOKING AT WHILE THEY ARE ON THE PHONE.
 *
 * Support answers questions about Flex Balance, a Trust Task, a Connect Code.
 * Until now a staff member had to know what those meant, or guess -- and a
 * guess given confidently to a customer becomes the platform's position.
 *
 * IT IS FETCHED FROM THE PLATFORM, NOT HELD HERE. The glossary is canon; canon
 * lives in the platform repository, and a copy in this one is the second
 * divergent definition this codebase keeps paying for. See lib/helpCentre.js
 * for why there is no cached fallback either.
 *
 * NO REASON GATE, FOR THE SAME REASON THE QUEUE ABOVE HAS NONE: nothing here
 * is anybody's record. It is the dictionary.
 */
const TheWordsCustomersUse = () => {
  const [book, setBook] = useState(null);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');

  useEffect(() => {
    let alive = true;
    fetchHelpCentre().then(({ data, error: err }) => {
      if (!alive) return;
      setBook(data);
      setError(err);
    });
    return () => { alive = false; };
  }, []);

  const hits = query.trim() ? searchTerms(book?.glossary, query).slice(0, 12) : [];

  return (
    <Card style={{ marginTop: 18 }}>
      <h3 style={{
        fontSize: 10.5, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase',
        color: O.inkFaint, margin: '0 0 6px',
      }}>The words customers use</h3>
      <p style={{
        fontFamily: SANS, fontSize: 12.5, lineHeight: 1.6, color: O.inkMuted, margin: '0 0 12px',
        maxWidth: '60ch',
      }}>
        The same glossary the customer is reading in their own app. Look a term
        up before answering, so the desk and the app say one thing.
      </p>

      {error && <Notice tone="warning">{error}</Notice>}

      {book && (
        <>
          <Field label="Search a term" htmlFor="glossary-search">
            <input
              id="glossary-search"
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Flex Balance, Trust Task, Connect Code…"
              style={textInputStyle(false)}
            />
          </Field>

          {query.trim() && hits.length === 0 && (
            <p style={{
              fontFamily: SANS, fontSize: 12.5, lineHeight: 1.6, color: O.inkMuted, margin: 0,
            }}>
              No entry for “{query.trim()}”. Thirteen everyday words — job, quote,
              rating, invoice, dispute — have not been written up yet, so this may
              be one of them rather than a mistyping.
            </p>
          )}

          {hits.map((g) => (
            <div key={g.term} style={{ padding: '10px 0', borderBottom: `1px solid ${O.hairline}` }}>
              <p style={{
                fontFamily: SANS, fontSize: 13, fontWeight: 700, color: O.ink, margin: '0 0 4px',
              }}>{g.term}</p>
              <p style={{
                fontFamily: SANS, fontSize: 12.5, lineHeight: 1.6, color: O.inkMuted,
                margin: 0, maxWidth: '62ch',
              }}>{g.definition}</p>
            </div>
          ))}

          {!query.trim() && (
            <p style={{
              fontFamily: SANS, fontSize: 11.5, color: O.inkFaint, margin: '4px 0 0',
            }}>
              {book.terms} terms, generated from the canon glossary and its
              Expansion Pack. Three of them the Expansion corrected — including
              Flex Balance, which Orchid does not hold.
            </p>
          )}
        </>
      )}
    </Card>
  );
};

export default function CustomerEnquiries() {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    const { data, error: err } = await fetchCustomerEnquiries();
    if (err) { setError(err); setRows([]); return; }
    setError(null);
    setRows(data);
  }, []);

  useEffect(() => { load(); }, [load]);

  if (rows === null && !error) return <LoadingPanel message="Reading the customer queue…" />;

  return (
    <>
      <h2 style={{
        fontSize: 10.5, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase',
        color: O.inkFaint, margin: '0 0 12px',
      }}>Customer enquiries · from Get in Touch</h2>

      {error && <Notice tone="critical">{error}</Notice>}

      <Queue
        rows={rows || []}
        settledLabel="Answered"
        stateOf={enquiryState}
        empty="Nobody has written in yet. An empty queue is a complete answer, not a fault."
        renderRow={(row, state) => (
          <EnquiryRow key={row.id} row={row} state={state} onChanged={load} />
        )}
      />

      <TheWordsCustomersUse />
    </>
  );
}
